import pluralize from 'pluralize'
import { partition, uniq } from 'lodash'
import { Nutritionix } from '@services'
import { mapWithConcurrency } from '@utils/promises'
import { BaseIngredient } from '@screens/Ingredients/types'
import {
  getThirdPartyIngredientId,
  normalizeNutrisenseItem,
  normalizeNutritionixItem,
  ALLOWED_SERVING_UNITS_TO_PLURALIZE,
} from '@screens/Ingredients/utils'
import { Ingredient, IngredientSource } from '@src/types'
import { NutritionixItemWithNutrients } from '@src/services/Nutritionix/types'

export const NORMAL_GRAM = 'Gram'
export const ABBREVIATED_GRAM = 'g'

export const isBaseIngredient = (item: unknown): item is BaseIngredient => {
  const { servingOptions } = item as BaseIngredient
  return !!servingOptions
}

export const pluralizeServingUnit = (servingUnit: string, amount?: number) => {
  const lastWord = servingUnit.slice(servingUnit.lastIndexOf(' ') + 1)
  const canBePluralized = ALLOWED_SERVING_UNITS_TO_PLURALIZE.has(lastWord.toLowerCase())

  return canBePluralized ? pluralize(servingUnit, amount) : servingUnit
}

export const ingredientsAreEqual = (
  a: Pick<BaseIngredient, 'id' | 'thirdPartyIngredientSource' | 'thirdPartyIngredientId'>,
  b: Pick<BaseIngredient, 'id' | 'thirdPartyIngredientSource' | 'thirdPartyIngredientId'>,
) => {
  // Nutrisense ingredients don't have `thirdPartyIngredientSource`
  if (!a.thirdPartyIngredientSource && !b.thirdPartyIngredientSource) {
    return a.id === b.id
  }

  return (
    a.thirdPartyIngredientSource === b.thirdPartyIngredientSource &&
    a.thirdPartyIngredientId === b.thirdPartyIngredientId
  )
}

export const shouldUpdateIngredientData = (ingredient: BaseIngredient | Ingredient) =>
  !isBaseIngredient(ingredient) || !ingredient.isUpToDate

export const updateIngredientsData = async (
  ingredients: (BaseIngredient | Ingredient)[],
): Promise<BaseIngredient[]> => {
  const ingredientsToUpdate = ingredients.filter(shouldUpdateIngredientData)

  if (ingredientsToUpdate.length === 0) {
    return ingredients as BaseIngredient[]
  }

  const thirdPartyIngredients = ingredientsToUpdate.filter((item) => item.thirdPartyIngredientId)
  const newIngredientsData = await getFullNutritionixData(thirdPartyIngredients)

  return ingredients.map((ingredient) => {
    if (!ingredientsToUpdate.includes(ingredient as BaseIngredient)) {
      return ingredient as BaseIngredient
    }

    const updatedIngredient = normalizeNutrisenseItem(ingredient as Ingredient)

    if (ingredient.thirdPartyIngredientSource === IngredientSource.Nutritionix) {
      // Legacy common ingredients have an id that contains the food name instead of the id (number)
      const isLegacyNutritionixIngredient = ingredient.thirdPartyIngredientId?.match(/^common\/\D+/)
      const detailedNutritionixIngredient = newIngredientsData.find((item) => {
        const itemId = isLegacyNutritionixIngredient
          ? `common/${item.food_name}`
          : getThirdPartyIngredientId(item)

        return itemId === ingredient.thirdPartyIngredientId
      })

      if (!detailedNutritionixIngredient) {
        return updatedIngredient
      }

      const baseIngredient = normalizeNutritionixItem(detailedNutritionixIngredient)

      const servingOptionForChosenServingUnit = baseIngredient.servingOptions.find(
        (option) => option.name === updatedIngredient.servingOptions[0].name,
      )
      const servingOptions = servingOptionForChosenServingUnit
        ? baseIngredient.servingOptions
        : [...baseIngredient.servingOptions, updatedIngredient.servingOptions[0]]

      return {
        ...updatedIngredient,
        id: baseIngredient.id,
        description: baseIngredient.description,
        servingOptions,
        imageUrl: baseIngredient.imageUrl,
        thirdPartyIngredientId: baseIngredient.thirdPartyIngredientId,
        thirdPartyIngredientSource: baseIngredient.thirdPartyIngredientSource,
      }
    }

    return updatedIngredient
  })
}

// A recent-ingredient refresh covers every ingredient on the screen, so firing
// the lookups all at once meant 40+ simultaneous requests — enough to time out
// the whole batch on a weak connection. Nutritionix answers each one in ~150ms,
// so a small window keeps the refresh fast without flooding the link.
const NUTRITIONIX_LOOKUP_CONCURRENCY = 4

type NutritionixLookup = () => Promise<
  NutritionixItemWithNutrients | NutritionixItemWithNutrients[] | undefined
>

export const getFullNutritionixData = async (ingredients: (BaseIngredient | Ingredient)[]) => {
  const allIngredientsAreFromNutritionix = ingredients.every(
    (ingredient) => ingredient.thirdPartyIngredientSource === IngredientSource.Nutritionix,
  )

  if (!allIngredientsAreFromNutritionix) {
    return [] as NutritionixItemWithNutrients[]
  }

  const [common, branded] = partition(ingredients, (item) =>
    item.thirdPartyIngredientId?.startsWith('common/'),
  )

  // The same ingredient can appear in several recent meals; one lookup answers
  // for all of them, since callers match the results back up by id.
  const commonLookups: NutritionixLookup[] = uniq(
    common.map((item) => item.description),
  ).map((description) => () => Nutritionix.searchItemsWithNutrients(description))
  // `partition` puts anything without a `common/` id on the branded side,
  // including ingredients with no third-party id at all — those would look up
  // `nix_item_id=undefined`, so drop them rather than asking for nothing.
  const brandedLookups: NutritionixLookup[] = uniq(
    branded.flatMap((item) =>
      item.thirdPartyIngredientId ? [item.thirdPartyIngredientId.replace('branded/', '')] : [],
    ),
  ).map((ingredientId) => () => Nutritionix.getBrandedItemById(ingredientId))

  const responses = await mapWithConcurrency(
    [...commonLookups, ...brandedLookups],
    NUTRITIONIX_LOOKUP_CONCURRENCY,
    (lookup) => lookup(),
  )

  return responses.flat().filter((item) => !!item) as NutritionixItemWithNutrients[]
}
